import axios, { AxiosRequestConfig } from 'axios';
import mem from 'mem';

import { useTokenStore } from '@/store/tokenStore';

const instance = axios.create({
	baseURL: 'https://api-kocsa.devermall.com/api/v1',
	headers: {
		ClientSecret: import.meta.env.VITE_APP_CLIENT_SECRET,
	},
});

const requestToken = async (refreshToken: string) => {
	try {
		const response = await instance.post('/front/requestToken', {
			refresh_token: refreshToken,
		});
		const accessToken = response.data?.data?.access_token;
		return accessToken;
	} catch (error) {
		if (axios.isAxiosError(error) && error.code !== 'ERR_NETWORK') {
			alert('토큰 오류, 다시 로그인해 주세요.');
			window.location.href = '/';
		}
	}
};

const requestTokenMem = mem(requestToken, { maxAge: 1000 });

instance.interceptors.request.use(
	(request) => {
		const tokenStore = useTokenStore.getState();
		request.headers.Authorization = tokenStore.isLogin() ? tokenStore.getAccessToken() : null;
		return request;
	},
	(error) => Promise.reject(error)
);

instance.interceptors.response.use(
	async (response) => {
		const tokenStore = useTokenStore.getState();
		const { config, data } = response;
		const error = data?.error;

		if (error) {
			const errorCode = Number(error.code);
			console.error(errorCode, error.message);

			if (errorCode === 84 || errorCode === 91) {
				alert(errorCode === 84 ? '세션이 만료되어 로그아웃됩니다.' : '다시 로그인을 진행해 주세요.');
				tokenStore.resetToken();

				// 네비게이션을 컴포넌트에서 처리하도록 수정
				window.location.href = '/auth/login';
			} else if (errorCode === 97) {
				const accessToken = await requestTokenMem(tokenStore.getRefreshToken() ?? '');
				if (accessToken) {
					config.headers.Authorization = accessToken;
					return instance(config);
				}
			}
		} else if (import.meta.env.DEV) {
			console.log(`${config.url} \n`, data);
		}
		return response;
	},
	(error) => {
		console.error(`${error.config?.url} \n`, error);
		if (error.code === 'ERR_NETWORK') {
			alert('네트워크 연결 에러입니다. 잠시 후 시도해 주세요');
		} else if (error.code === 'ERR_BAD_RESPONSE') {
			alert('서버가 응답하지 않습니다. 잠시 후 시도해 주세요');
		} else if (error.code === 'ECONNABORTED') {
			alert('요청시간을 초과했습니다. 잠시 후 시도해 주세요');
		} else if (error.code === 'ERR_BAD_REQUEST') {
			// alert('올바르지 않은 요청입니다. 잠시 후 시도해 주세요');
		} else {
			alert(error.code);
		}
		return Promise.reject(error);
	}
);

export default instance;

export const api = {
	get: <T>(url: string, params?: any, config?: AxiosRequestConfig) => instance.get<T>(url, { params, ...config }),
	post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.post<T>(url, data, config),
	put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.put<T>(url, data, config),
	patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.patch<T>(url, data, config),
	delete: <T>(url: string, params?: any, config?: AxiosRequestConfig) => instance.delete<T>(url, { params, ...config }),
};
